(function () {
  'use strict';

  // Liquid stamps the authoritative state on the checkout button itself, so it
  // travels with every section re-render. The JS never infers the state from the
  // notice being on screen: a root without the attribute is left untouched
  // rather than treated as "allowed".
  const STATE_SELECTOR = '[data-subscription-minimum-state]';
  const BLOCKED_SELECTOR = '[data-subscription-minimum-state="blocked"]';
  const IN_PLAY_SELECTOR = '[data-subscription-minimum-state="ok"], [data-subscription-minimum-state="blocked"]';
  const UPDATING_SELECTOR = '#main-cart-items.cart__items--disabled';
  const QUANTITY_SELECTOR = 'input[name="updates[]"]';
  const ROOT_SELECTORS = ['#mini-cart', '#main-cart-footer'];
  const CHECKOUT_SELECTOR = 'button[name="checkout"], .cart__checkout-button';
  const ACCELERATED_SELECTOR = '.additional-checkout-buttons';
  const BLOCKED_FLAG = 'data-subscription-minimum-blocked';
  const SETTLE_TIMEOUT = 8000;
  const SETTLE_INTERVAL = 100;

  // True while resolveThenSubmit is working through a click. Kept on the module
  // and not on the form, because a section re-render replaces the form element.
  let waiting = false;
  // Set for exactly one submit: the resubmit that has already been validated.
  let validated = false;
  // Invalidates callbacks from a cart check that was suspended while the page
  // navigated away. Safari can restore those promises and timers from the
  // back-forward cache; an old successful result must never resubmit the cart.
  let validationRun = 0;

  // The rendered state can be briefly stale during a section update or after a
  // back-forward-cache restore. The cart itself is the reliable source, so every
  // checkout attempt while this rule is in play is measured against /cart.js.
  async function cartClearsMinimum(minimumInCents) {
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';

    try {
      const response = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return null;

      const cart = await response.json();
      const lines = (cart.items || []).filter((item) => item.selling_plan_allocation);
      if (!lines.length) return true;

      const total = lines.reduce(
        (sum, item) => sum + item.selling_plan_allocation.price * item.quantity,
        0
      );
      return total >= minimumInCents;
    }
    catch (error) {
      return null;
    }
  }

  function readMinimum() {
    const state = document.querySelector(STATE_SELECTOR);
    return state ? Number(state.dataset.subscriptionMinimumAmount || 0) : 0;
  }

  function minimumIsInPlay() {
    return Boolean(document.querySelector(IN_PLAY_SELECTOR));
  }

  // Stopping the event outright, not just its default action: Shopify's
  // <shopify-accelerated-checkout-cart> also listens for the cart form's submit
  // and starts its own Shop Pay redirect, which preventDefault alone does not
  // hold back. This listener runs in the capture phase, so nothing else sees it.
  function refuse(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  function readState(root) {
    const state = root.querySelector(STATE_SELECTOR);
    if (!state) return null;
    return state.dataset.subscriptionMinimumState === 'blocked';
  }

  function driftedQuantityInputs(form) {
    return Array.from(form.querySelectorAll(QUANTITY_SELECTOR))
      .filter((input) => !input.disabled && input.value !== input.defaultValue);
  }

  // Scoped to the submitted form, not the document: the drawer and the cart page
  // each carry their own #main-cart-items and their own form#cart (duplicate ids
  // that predate this feature), and cart.js classes whichever #main-cart-items
  // getElementById finds first. Only the surface being submitted may speak here.
  function hasUnsettledCartEdit(form) {
    return Boolean(form.querySelector(UPDATING_SELECTOR) || driftedQuantityInputs(form).length);
  }

  // Normally the click that reaches checkout has already blurred the input and
  // queued cart.js' debounced update. A value the browser restored on its own
  // (back navigation, form restoration) has no update queued, so push it through
  // here — otherwise the drift would never settle and checkout would stay closed.
  // `flushed` guards against re-dispatching on an input that is already waiting:
  // cart.js debounces its change handler by 300ms, so a repeated dispatch would
  // restart that timer on every poll and the update would never run.
  function flushDriftedQuantities(form, flushed) {
    const inputs = driftedQuantityInputs(form).filter((input) => !flushed.has(input));
    if (!inputs.length) return;

    // cart.js focuses the field named by document.activeElement once the update
    // lands, and a line item holds no element named "checkout" — drop focus first.
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    // One at a time: cart.js addresses lines by index, and a line dropping to 0
    // reindexes the ones after it. The poll picks up whatever is left.
    flushed.add(inputs[0]);
    inputs[0].dispatchEvent(new Event('change', { bubbles: true }));
  }

  function syncCheckoutButtons(root, blocked) {
    root.querySelectorAll(CHECKOUT_SELECTOR).forEach((button) => {
      if (blocked) {
        button.setAttribute(BLOCKED_FLAG, '');
        button.setAttribute('aria-disabled', 'true');
      }
      else if (button.hasAttribute(BLOCKED_FLAG)) {
        button.removeAttribute(BLOCKED_FLAG);
        button.removeAttribute('aria-disabled');
      }
    });
  }

  // Shop Pay and Google Pay render as their own custom elements with their own
  // redirect logic, so they stay mounted at full size — a display:none wrapper
  // makes them initialise wrong — and are put out of reach with inert plus a
  // dimmed, pointer-events:none style. Their route into checkout ran off the
  // checkout control's own click and submit, which refuse() now stops outright.
  function syncAcceleratedButtons(root, blocked) {
    root.querySelectorAll(ACCELERATED_SELECTOR).forEach((wrapper) => {
      if (blocked) {
        wrapper.setAttribute(BLOCKED_FLAG, '');
        wrapper.inert = true;
      }
      else if (wrapper.hasAttribute(BLOCKED_FLAG)) {
        wrapper.removeAttribute(BLOCKED_FLAG);
        wrapper.inert = false;
      }
    });
  }

  function sync(root) {
    const blocked = readState(root);
    if (blocked === null) return;

    syncCheckoutButtons(root, blocked);
    syncAcceleratedButtons(root, blocked);
  }

  function syncAll() {
    ROOT_SELECTORS.forEach((selector) => {
      const root = document.querySelector(selector);
      if (root) sync(root);
    });
  }

  function observe() {
    ROOT_SELECTORS.forEach((selector) => {
      const root = document.querySelector(selector);
      if (!root) return;

      sync(root);
      // The drawer replaces its whole section and the cart page replaces
      // .js-contents and .cart__ctas, so a new state always arrives as a child
      // mutation. Attributes are not observed, which keeps this observer from
      // reacting to its own writes.
      new MutationObserver(() => sync(root)).observe(root, { childList: true, subtree: true });
    });
  }

  // The cart form is replaced wholesale by a section re-render, so the surface is
  // re-resolved on every tick instead of holding a reference that goes stale.
  function resolveForm(inDrawer) {
    return Array.from(document.querySelectorAll('form#cart'))
      .find((form) => Boolean(form.closest('#mini-cart')) === inDrawer) || null;
  }

  // The cart-page button lives in the footer section and points at the line-item
  // form through form="cart", so it is not necessarily a descendant of the form.
  function resolveCheckoutButton(form) {
    return Array.from(document.querySelectorAll(CHECKOUT_SELECTOR))
      .find((button) => button.form === form) || null;
  }

  function resetNavigationGuard() {
    validationRun += 1;
    waiting = false;
    validated = false;
    document.querySelectorAll(`${CHECKOUT_SELECTOR}.loading`)
      .forEach((button) => button.classList.remove('loading'));
  }

  // Brings the visible cart back in step with the server when the two disagree.
  // The drawer can be re-rendered from its section; the cart page cannot (its
  // sections are not addressable), so it is reloaded instead.
  function refreshSurface(inDrawer) {
    if (!inDrawer) {
      window.location.reload();
      return;
    }

    const drawer = document.querySelector('mini-cart');
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', 'mini-cart');

    fetch(url.toString())
      .then((response) => response.text())
      .then((html) => {
        const section = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
        if (!section) return;

        if (drawer) drawer.renderVersion = (drawer.renderVersion || 0) + 1;
        document.getElementById('mini-cart').innerHTML = section.innerHTML;
        syncAll();
      })
      .catch(() => window.location.reload());
  }

  // Resolves one checkout click: settle any pending quantity edit, then ask the
  // cart itself whether the minimum is met. Only a confirmed pass continues to
  // checkout, so neither a stale rendered state nor an in-flight edit can carry a
  // below-minimum subscription cart through.
  function resolveThenSubmit(initialForm, minimumInCents) {
    if (waiting) return;
    waiting = true;
    const run = ++validationRun;

    const inDrawer = Boolean(initialForm.closest('#mini-cart'));
    const startedAt = Date.now();
    const flushed = new WeakSet();

    const button = () => {
      const form = resolveForm(inDrawer);
      return form ? resolveCheckoutButton(form) : null;
    };
    const setLoading = (on) => {
      const el = button();
      if (el) el.classList.toggle('loading', on);
    };

    const finish = (clears) => {
      if (run !== validationRun) return;

      setLoading(false);
      waiting = false;
      syncAll();

      const form = resolveForm(inDrawer);
      if (clears && form) {
        validated = true;
        form.requestSubmit(resolveCheckoutButton(form) || undefined);
        return;
      }

      // Held back. If the markup still claims the cart is fine, it is out of date
      // — put the notice and the disabled button in front of the customer.
      if (clears === false && !document.querySelector(BLOCKED_SELECTOR)) refreshSurface(inDrawer);
    };

    const check = () => {
      cartClearsMinimum(minimumInCents).then((clears) => {
        if (run !== validationRun) return;

        // An unreachable cart falls back to the rendered state rather than
        // guessing: block if it says blocked, otherwise let the click proceed.
        if (clears === null) return finish(!document.querySelector(BLOCKED_SELECTOR));
        finish(clears);
      });
    };

    const poll = () => {
      if (run !== validationRun) return;

      const form = resolveForm(inDrawer);
      if (!form) {
        waiting = false;
        setLoading(false);
        return;
      }

      if (hasUnsettledCartEdit(form)) {
        if (Date.now() - startedAt > SETTLE_TIMEOUT) return finish(false);
        setLoading(true);
        flushDriftedQuantities(form, flushed);
        window.setTimeout(poll, SETTLE_INTERVAL);
        return;
      }

      check();
    };

    setLoading(true);
    flushDriftedQuantities(initialForm, flushed);
    window.setTimeout(poll, SETTLE_INTERVAL);
  }

  // Own the first checkout activation whenever the subscription rule is in play.
  // Shopify's accelerated-checkout element starts work from this click before the
  // submit event. If the click is allowed through while our cart check is pending,
  // Safari can navigate first, suspend the check, then resume its successful
  // result after the customer comes back and send the now-invalid cart to checkout.
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest(CHECKOUT_SELECTOR);
    if (!button || !minimumIsInPlay()) return;

    const form = button.form || button.closest('form#cart');
    if (!(form instanceof HTMLFormElement) || form.id !== 'cart') return;

    refuse(event);
    syncAll();

    const minimum = Number(button.dataset.subscriptionMinimumAmount || readMinimum());
    if (!minimum) return;

    // A blocked surface stays put. If its HTML is merely stale, refresh it after
    // the authoritative cart confirms that checkout is now allowed.
    if (document.querySelector(BLOCKED_SELECTOR)) {
      const inDrawer = Boolean(form.closest('#mini-cart'));
      cartClearsMinimum(minimum).then((clears) => {
        if (clears === true) refreshSurface(inDrawer);
      });
      return;
    }

    resolveThenSubmit(form, minimum);
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.id !== 'cart') return;

    // A named submitter other than checkout is a cart update, which stays allowed.
    const submitter = event.submitter;
    if (submitter && submitter.name !== 'checkout') return;

    // The resubmit issued after a successful check. Only the browser's native
    // form POST may act on it: shopify-accelerated-checkout-cart also watches
    // the cart form's submit and starts its own asynchronous redirect from it,
    // which Safari would freeze into the back-forward cache and resume when the
    // customer returns from checkout — forwarding whatever the cart holds then.
    if (validated) {
      validated = false;
      event.stopImmediatePropagation();
      return;
    }

    // A blocked cart is refused without waiting on the network. If the cart has
    // since risen above the minimum and only the markup is behind, put the
    // corrected cart in front of the customer so their next click works.
    if (document.querySelector(BLOCKED_SELECTOR)) {
      refuse(event);
      syncAll();

      const inDrawer = Boolean(form.closest('#mini-cart'));
      cartClearsMinimum(readMinimum()).then((clears) => {
        if (clears === true) refreshSurface(inDrawer);
      });
      return;
    }

    // No subscription minimum in play for this cart, so nothing to measure.
    if (!minimumIsInPlay()) return;

    const minimum = readMinimum();
    if (!minimum) return;

    refuse(event);
    resolveThenSubmit(form, minimum);
  }, true);

  // Page lifecycle transitions must cancel rather than resume an earlier cart
  // check. This is especially important on mobile Safari, which restores timers
  // and promises when returning from Checkout through its back-forward cache.
  window.addEventListener('pagehide', resetNavigationGuard);
  window.addEventListener('pageshow', () => {
    resetNavigationGuard();
    syncAll();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observe);
  }
  else {
    observe();
  }
})();
